import { useMutation, useQueryClient } from "@tanstack/react-query";
import { httpClient } from "@/shared/api/http-client";
import { LEDGER_QUERY_KEY } from "./constants";
import type { ManualLedgerEntry, ManualLedgerEntryInput } from "./types";

/**
 * Hand-written fetch — see CLAUDE.md → TD-07. `POST /api/admin/ledger/refund`
 * and `…/chargeback` (API-FIN-7, WANDERFIT_ADMIN + WANDERFIT_FINANCE) post an
 * accounting-only `MANUAL_REFUND` / `CHARGEBACK` entry — **no Stripe call**.
 * Idempotent when an `idempotencyKey` (UUID) is supplied. The `type` selects
 * the route; everything else is the shared `ManualLedgerEntryDto` body.
 */
async function postLedgerCorrection({
  type,
  ...body
}: ManualLedgerEntryInput): Promise<ManualLedgerEntry> {
  return httpClient<ManualLedgerEntry>(`/api/admin/ledger/${type}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function usePostLedgerCorrection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postLedgerCorrection,
    // A correction posts a new ledger entry, so the cash-ledger viewer
    // (API-FIN-5) is now stale — invalidate it so the entry shows up without
    // waiting out `staleTime` or a manual refresh.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: LEDGER_QUERY_KEY }),
  });
}
